import React, { useEffect, useState } from "react";
import api from "../../../services/api";
import Logo from "../../../common/assets/blue-logo.svg";

const APP_STORE_URL = "https://apps.apple.com/fr/app/fideliz-fr/id6538715213";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.fideliz.app";

/**
 * Collecte les données de fingerprint du navigateur
 */
const collectFingerprint = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
  const isAndroid = /android/i.test(userAgent);

  let deviceModel = "unknown";
  if (isIOS) {
    const iosMatch = userAgent.match(/(iPhone|iPad|iPod)[\s;].*?OS\s(\d+)/);
    deviceModel = iosMatch ? iosMatch[1] : "iOS Device";
  } else if (isAndroid) {
    const androidMatch = userAgent.match(/;\s*([^;]+)\s+Build/);
    deviceModel = androidMatch ? androidMatch[1].trim() : "Android Device";
  }

  let browser = "unknown";
  if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) {
    browser = "Safari";
  } else if (userAgent.includes("Chrome")) {
    browser = "Chrome";
  } else if (userAgent.includes("Firefox")) {
    browser = "Firefox";
  }

  return {
    platform: isIOS ? "ios" : isAndroid ? "android" : "web",
    device_model: deviceModel,
    browser: browser,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language || navigator.userLanguage,
  };
};

/**
 * Envoie le fingerprint au serveur pour le deferred deep linking
 */
const storeDeferredLink = async (storeCode) => {
  try {
    const fingerprintData = collectFingerprint();
    await api.post("/deferred-link/store", {
      store_code: storeCode,
      ...fingerprintData,
    });
    console.log("[QRCode] Deferred link stocké", { storeCode, ...fingerprintData });
  } catch (error) {
    console.error("[QRCode] Erreur stockage deferred link:", error);
  }
};

/**
 * Détecte si on est dans un navigateur in-app (Instagram, Facebook, etc.)
 */
const isInAppBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return (
    userAgent.includes("Instagram") ||
    userAgent.includes("FBAN") || // Facebook
    userAgent.includes("FBAV") || // Facebook
    userAgent.includes("Twitter") ||
    userAgent.includes("Line") ||
    userAgent.includes("Snapchat") ||
    userAgent.includes("TikTok")
  );
};

function QRCode({ creatorId }) {
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [storeCode, setStoreCode] = useState(null);
  const [storeUrl, setStoreUrl] = useState(null);

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        // 1. Récupérer le store_code depuis l'API
          const response = await api.get(`/qr-codes/optician/${creatorId}/store-code`);
          const { store_code } = response.data;

          if (!store_code) {
            throw new Error("Code boutique non trouvé");
          }

        setStoreCode(store_code);

        // 2. Stocker le deferred link (fingerprint) côté serveur
        await storeDeferredLink(store_code);

        // 3. Stocker dans localStorage (fallback local)
        localStorage.setItem("fideliz_store_code", store_code);
        localStorage.setItem("fideliz_redirect_timestamp", Date.now().toString());

        // 4. Détecter le device
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
        const isAndroid = /android/i.test(userAgent);

        // Desktop - afficher les boutons de téléchargement
        if (!isIOS && !isAndroid) {
          setStatus("desktop");
          return;
        }

        // Définir l'URL du store
        const currentStoreUrl = isIOS ? APP_STORE_URL : PLAY_STORE_URL;
        setStoreUrl(currentStoreUrl);

        // 5. Vérifier si on est dans un navigateur in-app
        if (isInAppBrowser()) {
          // Dans un navigateur in-app, les deep links ne fonctionnent souvent pas
          // Afficher un message pour ouvrir dans le navigateur externe
          setStatus("inapp");
          return;
        }

        setStatus("redirecting");

        // 6. Construire le deep link
        const deepLink = `fideliz://register?storeCode=${encodeURIComponent(store_code)}`;

        // 7. Essayer d'ouvrir l'app via le deep link
        const startTime = Date.now();
        
        // Ouvrir le deep link
        window.location.href = deepLink;

        // 8. Si après 1.5 sec on est toujours là, rediriger vers le store
        setTimeout(() => {
          // Vérifier qu'on n'a pas quitté la page (app ouverte)
          const elapsed = Date.now() - startTime;
          if (elapsed < 2000 && !document.hidden) {
            console.log("[QRCode] App non installée, redirection vers le store");
            window.location.href = currentStoreUrl;
          }
        }, 1500);

      } catch (err) {
        console.error("Erreur lors de la redirection:", err);
        setError(err.response?.data?.message || err.message || "Erreur inconnue");
        setStatus("error");
      }
    };

    handleRedirect();
  }, [creatorId]);

  // Affichage selon le status
  if (status === "loading") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-4">
        <img src={Logo} alt="Fideliz" className="h-8 mb-8" />
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-gray-600">Chargement...</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-4">
        <img src={Logo} alt="Fideliz" className="h-8 mb-8" />
        <div className="bg-white rounded-lg shadow-md p-6 max-w-md text-center">
          <h1 className="text-xl font-semibold text-red-600 mb-4">Erreur</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">
            Veuillez contacter le support Fideliz pour plus d'informations.
          </p>
        </div>
      </div>
    );
  }

  // Navigateur in-app détecté
  if (status === "inapp") {
    const userAgent = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-4">
        <img src={Logo} alt="Fideliz" className="h-8 mb-8" />
        <div className="bg-white rounded-lg shadow-md p-6 max-w-md text-center">
          <h1 className="text-xl font-semibold mb-4">Ouvrir dans votre navigateur</h1>
          <p className="text-gray-600 mb-6">
            Pour télécharger l'application, ouvrez ce lien dans {isIOS ? "Safari" : "Chrome"}.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Appuyez sur les 3 points (⋯) puis "Ouvrir dans {isIOS ? "Safari" : "le navigateur"}"
          </p>
          {storeCode && (
            <div className="bg-blue-50 rounded-lg p-4 mt-4">
              <p className="text-sm text-gray-600 mb-2">Votre code boutique :</p>
              <p className="text-2xl font-bold text-blue-600">{storeCode}</p>
              <p className="text-xs text-gray-500 mt-2">
                Gardez ce code, vous en aurez besoin lors de l'inscription
              </p>
            </div>
          )}
          {storeUrl && (
            <a
              href={storeUrl}
              className="mt-6 inline-block bg-blue-500 text-white px-6 py-3 rounded-lg font-medium"
            >
              Télécharger l'application
            </a>
          )}
        </div>
      </div>
    );
  }

  if (status === "desktop") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-4">
        <img src={Logo} alt="Fideliz" className="h-8 mb-8" />
        <div className="bg-white rounded-lg shadow-md p-6 max-w-md text-center">
          <h1 className="text-xl font-semibold mb-4">Téléchargez l'application Fideliz</h1>
          <p className="text-gray-600 mb-6">
            Pour vous inscrire, veuillez télécharger notre application mobile.
          </p>
          {storeCode && (
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-2">Votre code boutique :</p>
              <p className="text-2xl font-bold text-blue-600">{storeCode}</p>
              <p className="text-xs text-gray-500 mt-2">
                Notez ce code, vous en aurez besoin lors de l'inscription
              </p>
            </div>
          )}
          <div className="flex flex-col space-y-4">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/40/Download_on_the_App_Store_Badge_FRCA_RGB_blk.svg/254px-Download_on_the_App_Store_Badge_FRCA_RGB_blk.svg.png"
                alt="Télécharger sur l'App Store"
                className="w-40"
              />
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center"
            >
              <img
                src="https://global-img.gamergen.com/bouton-telechargement-google-play-store_0362011B00831632.png"
                alt="Télécharger sur Google Play"
                className="w-40"
              />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Status "redirecting"
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-4">
      <img src={Logo} alt="Fideliz" className="h-8 mb-8" />
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
      <p className="text-gray-600 text-center">
        Redirection vers l'application Fideliz...
      </p>
      <p className="text-sm text-gray-400 mt-2">
        Si l'application ne s'ouvre pas, vous serez redirigé vers le store.
      </p>
      {storeCode && (
        <div className="bg-white rounded-lg shadow-md p-4 mt-6 text-center">
          <p className="text-xs text-gray-500 mb-1">Votre code boutique :</p>
          <p className="text-lg font-bold text-blue-600">{storeCode}</p>
        </div>
      )}
    </div>
  );
}

export default QRCode;
